// src/pages/fourd/Project4DUploadPage.jsx
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import ThreeViewer from "../../components/viewer/viewer3d/ThreeViewer";
import FileTreePanel from "../../components/common/papers/FileTreePanel";
import {
  uploadPaperFile,
  deletePaperFile,
  createPapersFolder,
  deletePapersFolder, // ✅ 폴더 삭제 추가
} from "../../api/papers";

const Project4DUploadPage = () => {
  const { id: projectId } = useParams();

  const [rootFolder, setRootFolder] = useState(null); // 4DSimulation 루트(옵션)
  const [selectedFolder, setSelectedFolder] = useState(null); // 선택된 폴더
  const [selectedFile, setSelectedFile] = useState(null); // 미리볼 glTF
  const [uploadFiles, setUploadFiles] = useState([]); // gltf + bin
  const [newFolderName, setNewFolderName] = useState(""); // 새 폴더명
  const [treeRefreshKey, setTreeRefreshKey] = useState(0); // 트리 리로드용
  const [isUploading, setIsUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);

  const handleFileInputChange = (e) => {
    setUploadFiles(Array.from(e.target.files || []));
  };

  // ✅ 새 폴더 생성 (선택 폴더 아래, 없으면 4DSimulation 아래)
  const handleCreateFolder = async () => {
    if (!projectId) {
      alert("프로젝트 정보가 없습니다.");
      return;
    }
    if (!newFolderName.trim()) {
      alert("폴더 이름을 입력하세요.");
      return;
    }

    const parentFolder = selectedFolder || rootFolder;
    if (!parentFolder) {
      alert("4DSimulation 폴더 정보를 불러오지 못했습니다.");
      return;
    }

    try {
      setCreatingFolder(true);
      await createPapersFolder({
        project: projectId,
        name: newFolderName.trim(),
        parent: parentFolder.id,
      });
      alert("새 폴더가 생성되었습니다.");
      setNewFolderName("");
      setTreeRefreshKey((prev) => prev + 1);
    } catch (e) {
      console.error(e.response?.data || e.message);
      alert("폴더 생성 중 오류가 발생했습니다.");
    } finally {
      setCreatingFolder(false);
    }
  };

  // ✅ 선택된 폴더 삭제 (업로드 페이지에서 처리)
  const handleDeleteFolder = async () => {
    if (!selectedFolder) {
      alert("삭제할 폴더를 선택하세요.");
      return;
    }

    if (
      !window.confirm(
        `"${selectedFolder.name}" 폴더를 삭제할까요?\n하위 폴더/파일도 함께 삭제됩니다.`
      )
    ) {
      return;
    }

    try {
      await deletePapersFolder(selectedFolder.id);
      alert("폴더가 삭제되었습니다.");
      setSelectedFolder(null);
      setTreeRefreshKey((prev) => prev + 1);
    } catch (e) {
      console.error(e.response?.data || e.message);
      alert("폴더 삭제 중 오류가 발생했습니다.");
    }
  };

  const handleUpload = async () => {
    if (!projectId) {
      alert("프로젝트 정보가 없습니다.");
      return;
    }
    if (!selectedFolder) {
      alert(
        "왼쪽 트리에서 업로드할 폴더를 선택하세요.\n(4DSimulation 하위 폴더 선택)"
      );
      return;
    }
    if (!uploadFiles.length) {
      alert("gltf와 bin 파일을 선택하세요.");
      return;
    }

    const fileMap = Object.fromEntries(
      uploadFiles.map((f) => [f.name.split(".").pop().toLowerCase(), f])
    );
    const gltfFile = fileMap["gltf"];
    const binFile = fileMap["bin"];

    if (!gltfFile || !binFile) {
      alert("gltf 파일과 bin 파일을 모두 선택해야 합니다.");
      return;
    }

    try {
      setIsUploading(true);

      const folderId = selectedFolder.id;

      // 1) gltf 업로드
      const payloadGltf = {
        project: projectId,
        folder: folderId,
        file: gltfFile,
        name: gltfFile.name,
      };
      const gltfRes = await uploadPaperFile(payloadGltf);

      // 2) bin 업로드
      const payloadBin = {
        project: projectId,
        folder: folderId,
        file: binFile,
        name: binFile.name,
      };
      await uploadPaperFile(payloadBin);

      alert("gltf + bin 파일이 자료실에 업로드되었습니다.");

      setTreeRefreshKey((prev) => prev + 1);

      const uploadedGltf = gltfRes.data;
      setSelectedFile(uploadedGltf);
      setUploadFiles([]);
    } catch (e) {
      console.error(e.response?.data || e.message);
      alert("업로드 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteSelectedFile = async () => {
    if (!selectedFile?.id) {
      alert("삭제할 파일이 선택되지 않았습니다.");
      return;
    }
    if (!window.confirm("이 glTF 파일을 삭제할까요?")) return;

    try {
      await deletePaperFile(selectedFile.id);
      alert("파일이 삭제되었습니다.");
      setTreeRefreshKey((prev) => prev + 1);
      setSelectedFile(null);
    } catch (e) {
      console.error(e.response?.data || e.message);
      alert("파일 삭제 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-[calc(100vh-64px)]">
      {/* 상단 헤더 */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          4D 시뮬레이션 업로드
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          프로젝트 ID:{" "}
          <span className="font-medium text-gray-700">{projectId}</span>
        </p>
        <p className="mt-2 text-xs text-gray-500">
          4DSimulation 폴더 아래에 층/구역별 폴더를 만들고, 각 폴더에 glTF/BIN
          파일을 업로드하여 관리할 수 있습니다.
        </p>
      </div>

      {/* 본문 레이아웃 (4D 보기 페이지처럼 꽉 차게) */}
      <div className="flex gap-5 h-[700px]">
        {/* 왼쪽: 트리 */}
        <div className="w-1/4 min-w-[260px] border rounded-2xl p-4 shadow-sm bg-white flex flex-col">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">
                4D용 파일 트리 (4DSimulation)
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                4DSimulation 하위 폴더를 자유롭게 추가해 분류할 수 있습니다.
              </p>
            </div>
            {/* ✅ 폴더 삭제 버튼 (업로드 페이지에서 처리) */}
            <button
              type="button"
              onClick={handleDeleteFolder}
              disabled={!selectedFolder}
              className="px-3 py-1.5 rounded-lg border border-red-300 text-[11px] text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              폴더 삭제
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <FileTreePanel
              key={treeRefreshKey}
              selectionMode="single"
              rootFolderName="4DSimulation"
              title=""
              fileExtensionFilter={["gltf"]}
              onPick={(file) => setSelectedFile(file)}
              onFolderSelect={(folder) => {
                setSelectedFolder(folder);
                // 필요하면 4DSimulation 루트도 여기서 세팅
                if (!rootFolder && folder.name === "4DSimulation") {
                  setRootFolder(folder);
                }
              }}
            />
          </div>
        </div>

        {/* 가운데: 업로드 + 새 폴더 */}
        <div className="w-1/4 min-w-[260px] border rounded-2xl p-4 shadow-sm bg-white flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            glTF + BIN 업로드
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            왼쪽 트리에서 업로드할 폴더를 선택하거나, 아래에서 새 폴더를 만들어
            분류할 수 있습니다.
          </p>

          <div className="mb-4 text-xs text-gray-600 space-y-1">
            <div>
              선택된 폴더:{" "}
              {selectedFolder ? (
                <span className="font-medium">{selectedFolder.name}</span>
              ) : rootFolder ? (
                <span className="font-medium text-gray-700">
                  {rootFolder.name} (루트)
                </span>
              ) : (
                <span className="text-gray-400">
                  트리에서 폴더를 선택하거나, 4DSimulation을 먼저 클릭하세요.
                </span>
              )}
            </div>

            {/* 새 폴더 생성 UI */}
            <div className="mt-3">
              <div className="mb-1 text-[11px] text-gray-500">
                새 폴더 생성 (선택된 폴더 또는 4DSimulation 아래)
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="예: 1층, 2층, 코어 영역..."
                  className="flex-1 border rounded-lg px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  onClick={handleCreateFolder}
                  disabled={creatingFolder}
                  className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs hover:bg-slate-800 disabled:opacity-50"
                >
                  {creatingFolder ? "생성중..." : "새 폴더"}
                </button>
              </div>
            </div>
          </div>

          {/* 파일 선택/업로드 */}
          <div className="space-y-3">
            <input
              type="file"
              multiple
              accept=".gltf,.bin"
              onChange={handleFileInputChange}
              className="block w-full text-sm text-slate-700"
            />
            {uploadFiles.length > 0 && (
              <div className="text-xs text-gray-600">
                선택된 파일:
                <ul className="list-disc list-inside">
                  {uploadFiles.map((f) => (
                    <li key={f.name}>{f.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-auto pt-4">
            <button
              onClick={handleUpload}
              disabled={isUploading}
              className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white shadow hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? "업로드 중..." : "선택 폴더에 업로드"}
            </button>
          </div>
        </div>

        {/* 오른쪽: 미리보기 */}
        <div className="flex-1 border rounded-2xl p-4 shadow-sm bg-white flex flex-col">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">모델 미리보기</h3>
            <span className="text-xs text-gray-500">
              트리에서 glTF 파일을 선택하면 아래 뷰어에서 확인 및 삭제할 수
              있습니다.
            </span>
          </div>

          <div className="flex-1 relative rounded-xl bg-gray-100 overflow-hidden">
            {selectedFile ? (
              <ThreeViewer gltfUrl={selectedFile.file} />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-sm text-gray-500">
                  왼쪽 트리에서 glTF 파일을 선택하면 이 영역에 미리보기가
                  표시됩니다.
                </p>
              </div>
            )}
          </div>

          {selectedFile && (
            <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
              <div>
                선택된 파일:{" "}
                <span className="font-medium">{selectedFile.name}</span>
              </div>
              <button
                onClick={handleDeleteSelectedFile}
                className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50"
              >
                파일 삭제
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Project4DUploadPage;
